import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

type ArrivalDay = "thursday" | "friday" | "saturday";
type AlcoholLevel = "none" | "little" | "moderate" | "more_the_merrier";

type MePayload = {
  ok: true;
  household: {
    id: string;
    phone_e164: string;
    label: string | null;
    created_at: string;
  };
  people: Array<{
    id: string;
    full_name: string;
    phone_e164: string | null;
    is_child: boolean;
    child_age_years: number | null;
    is_avec: boolean;
    created_by_guest: boolean;
    created_at: string;

    rsvp: null | {
      ceremony_attending: boolean | null;
      dinner_attending: boolean | null;
      arrival: ArrivalDay | null;
      dietary_notes: string | null;
      alcohol: AlcoholLevel | null;
      submitted_at: string | null;
      updated_at: string | null;
    };

    accommodation: null | {
      start_day: ArrivalDay | null;
      cottage_preferences: string | null;
      updated_at: string | null;
    };
  }>;
};

type ApiError = { status: number; message: string };

const COTTAGES_FULL = true;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
    credentials: "include",
  });

  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    // ignore
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    if (isRecord(data) && typeof data.error === "string") {
      message = data.error;
    }
    throw { status: res.status, message } satisfies ApiError;
  }

  return data as T;
}

type PersonDraft = {
  id: string;
  full_name: string;
  is_child: boolean;
  is_avec: boolean;
  child_age_years: number | null;

  ceremony_attending: boolean | null;
  dinner_attending: boolean | null;
  arrival: ArrivalDay | null;
  dietary_notes: string;

  alcohol: AlcoholLevel | null;

  wants_cottage: boolean;
  cottage_start_day: ArrivalDay | null;
  cottage_preferences: string;
};

type AvecDraft = {
  full_name: string;
  phone_e164: string;
  ceremony_attending: boolean | null;
  dinner_attending: boolean | null;
  arrival: ArrivalDay | null;
  dietary_notes: string;
  alcohol: AlcoholLevel | null;

  wants_cottage: boolean;
  cottage_start_day: ArrivalDay | null;
  cottage_preferences: string;
};

function toDraft(me: MePayload): PersonDraft[] {
  return me.people.map((p) => ({
    id: p.id,
    full_name: p.full_name,
    is_child: p.is_child,
    is_avec: p.is_avec,
    child_age_years: p.child_age_years ?? null,

    ceremony_attending: p.rsvp?.ceremony_attending ?? null,
    dinner_attending: p.rsvp?.dinner_attending ?? null,
    arrival: p.rsvp?.arrival ?? null,
    dietary_notes: p.rsvp?.dietary_notes ?? "",

    alcohol: p.rsvp?.alcohol ?? null,

    wants_cottage:
      p.accommodation?.start_day != null ||
      p.accommodation?.cottage_preferences != null,
    cottage_start_day: p.accommodation?.start_day ?? null,
    cottage_preferences: p.accommodation?.cottage_preferences ?? "",
  }));
}

function boolSelectValue(v: boolean | null): "yes" | "no" | "unset" {
  if (v === true) return "yes";
  if (v === false) return "no";
  return "unset";
}

function boolFromSelect(v: string): boolean | null {
  if (v === "yes") return true;
  if (v === "no") return false;
  return null;
}

function parseAge(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

const ARRIVAL_OPTIONS: Array<{ value: ArrivalDay; label: string }> = [
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
];

const ALCOHOL_OPTIONS: Array<{ value: AlcoholLevel; label: string }> = [
  { value: "none", label: "No alcohol" },
  { value: "little", label: "Little" },
  { value: "moderate", label: "Moderate" },
  { value: "more_the_merrier", label: "The more the merrier" },
];

function makeEmptyAvecDraft(): AvecDraft {
  return {
    full_name: "",
    phone_e164: "",
    ceremony_attending: null,
    dinner_attending: null,
    arrival: null,
    dietary_notes: "",
    alcohol: null,
    wants_cottage: false,
    cottage_start_day: null,
    cottage_preferences: "",
  };
}

export function RsvpPage() {
  const [me, setMe] = useState<MePayload | null>(null);
  const [draft, setDraft] = useState<PersonDraft[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [successText, setSuccessText] = useState<string | null>(null);

  // New child form
  const [childName, setChildName] = useState<string>("");
  const [childAge, setChildAge] = useState<string>("");

  // Avec UI state
  const [avecEnabled, setAvecEnabled] = useState<boolean>(false);
  const [newAvec, setNewAvec] = useState<AvecDraft>(makeEmptyAvecDraft);

  async function loadMe(): Promise<void> {
    setErrorText(null);
    setSuccessText(null);
    setLoading(true);
    try {
      const payload = await apiJson<MePayload>("/api/me");
      setMe(payload);
      const d = toDraft(payload);
      setDraft(d);

      const hasAvec = payload.people.some((p) => p.is_avec);
      setAvecEnabled(hasAvec);

      // When avec already exists, keep newAvec clean.
      setNewAvec(makeEmptyAvecDraft());
    } catch (e: any) {
      setErrorText(e?.message ?? "Failed to load RSVP data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadMe();
  }, []);

  function updatePerson(personId: string, patch: Partial<PersonDraft>) {
    setDraft((prev) =>
      prev.map((p) => (p.id === personId ? { ...p, ...patch } : p)),
    );
  }

  const existingAvec = useMemo<PersonDraft | null>(() => {
    const a = draft.find((p) => p.is_avec);
    return a ?? null;
  }, [draft]);

  const nonAvecDraft = useMemo<PersonDraft[]>(
    () => draft.filter((p) => !p.is_avec),
    [draft],
  );

  const adultCount = useMemo<number>(() => {
    if (!me) return 0;
    // "adult" means invited adult, not child, not avec
    return me.people.filter((p) => !p.is_child && !p.is_avec).length;
  }, [me]);

  const avecAllowed = adultCount === 1;

  function hadCottageBooking(personId: string): boolean {
    if (!me) return false;
    const acc = me.people.find((p) => p.id === personId)?.accommodation;
    return acc?.start_day != null || acc?.cottage_preferences != null;
  }

  const hasChanges = useMemo(() => {
    if (!me) return false;

    // Compare current server state to current draft (non-avec)
    const currentNonAvec = JSON.stringify(
      toDraft(me).filter((p) => !p.is_avec),
    );
    const nowNonAvec = JSON.stringify(nonAvecDraft);

    // Avec changes: enabled flag + (either existing avec draft, or newAvec fields)
    const currentHasAvec = me.people.some((p) => p.is_avec);
    const currentAvecDraft = toDraft(me).find((p) => p.is_avec) ?? null;

    const currentAvecComparable = JSON.stringify({
      enabled: currentHasAvec,
      existing: currentAvecDraft,
    });

    const nowAvecComparable = JSON.stringify({
      enabled: avecEnabled,
      existing: existingAvec,
      newAvec: avecEnabled && !existingAvec ? newAvec : null,
    });

    return (
      currentNonAvec !== nowNonAvec ||
      currentAvecComparable !== nowAvecComparable
    );
  }, [me, nonAvecDraft, avecEnabled, existingAvec, newAvec]);

  async function saveAll(): Promise<void> {
    if (!me) return;

    setErrorText(null);
    setSuccessText(null);

    // UI guard (backend enforces too)
    if (avecEnabled && !existingAvec && !avecAllowed) {
      setErrorText(
        "Plus-one is only allowed for households with exactly one adult.",
      );
      return;
    }

    setSaving(true);

    try {
      const peoplePayload = nonAvecDraft.map((p) => ({
        id: p.id,
        rsvp: {
          ceremony_attending: p.ceremony_attending,
          dinner_attending: p.dinner_attending,
          arrival: p.arrival,
          dietary_notes: p.dietary_notes.trim() ? p.dietary_notes : null,
          alcohol: p.is_child ? null : p.alcohol, // ignore alcohol for kids
        },
        accommodation: p.wants_cottage
          ? {
              start_day: p.cottage_start_day,
              cottage_preferences: p.cottage_preferences.trim()
                ? p.cottage_preferences
                : null,
            }
          : null,
      }));

      // Avec payload
      let avecPayload: any = undefined;

      if (avecEnabled) {
        if (existingAvec) {
          // Update existing avec (and keep it enabled)
          avecPayload = {
            enabled: true,
            // Name/phone for existing avec: you may later add explicit editing; for now keep existing name
            full_name: existingAvec.full_name,
            phone_e164: null,
            rsvp: {
              ceremony_attending: existingAvec.ceremony_attending,
              dinner_attending: existingAvec.dinner_attending,
              arrival: existingAvec.arrival,
              dietary_notes: existingAvec.dietary_notes.trim()
                ? existingAvec.dietary_notes
                : null,
              alcohol: existingAvec.alcohol,
            },
            accommodation: existingAvec.wants_cottage
              ? {
                  start_day: existingAvec.cottage_start_day,
                  cottage_preferences: existingAvec.cottage_preferences.trim()
                    ? existingAvec.cottage_preferences
                    : null,
                }
              : null,
          };
        } else {
          // Create new avec
          avecPayload = {
            enabled: true,
            full_name: newAvec.full_name.trim() ? newAvec.full_name : null,
            phone_e164: newAvec.phone_e164.trim() ? newAvec.phone_e164 : null,
            rsvp: {
              ceremony_attending: newAvec.ceremony_attending,
              dinner_attending: newAvec.dinner_attending,
              arrival: newAvec.arrival,
              dietary_notes: newAvec.dietary_notes.trim()
                ? newAvec.dietary_notes
                : null,
              alcohol: newAvec.alcohol,
            },
            accommodation: newAvec.wants_cottage
              ? {
                  start_day: newAvec.cottage_start_day,
                  cottage_preferences: newAvec.cottage_preferences.trim()
                    ? newAvec.cottage_preferences
                    : null,
                }
              : null,
          };
        }
      } else if (existingAvec) {
        // Disable/remove existing avec
        avecPayload = { enabled: false };
      }

      const body = {
        people: peoplePayload,
        ...(avecPayload !== undefined ? { avec: avecPayload } : {}),
      };

      const payload = await apiJson<MePayload>("/api/rsvp/submit", {
        method: "POST",
        body: JSON.stringify(body),
      });

      setMe(payload);
      const d = toDraft(payload);
      setDraft(d);

      const hasAvecNow = payload.people.some((p) => p.is_avec);
      setAvecEnabled(hasAvecNow);
      setNewAvec(makeEmptyAvecDraft());

      setSuccessText("Saved!");
    } catch (e: any) {
      setErrorText(e?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function addChild(): Promise<void> {
    const age = parseAge(childAge);

    setErrorText(null);
    setSuccessText(null);
    setSaving(true);

    try {
      const payload = await apiJson<MePayload>("/api/rsvp/submit", {
        method: "POST",
        body: JSON.stringify({
          people: [],
          new_children: [
            {
              full_name: childName.trim() ? childName.trim() : null,
              child_age_years: age,
            },
          ],
        }),
      });

      setMe(payload);
      setDraft(toDraft(payload));

      const hasAvecNow = payload.people.some((p) => p.is_avec);
      setAvecEnabled(hasAvecNow);

      setChildName("");
      setChildAge("");
      setSuccessText("Child added!");
    } catch (e: any) {
      setErrorText(e?.message ?? "Failed to add child");
    } finally {
      setSaving(false);
    }
  }

  async function deletePerson(personId: string, label: string): Promise<void> {
    const ok = window.confirm(`Delete ${label}?`);
    if (!ok) return;

    setErrorText(null);
    setSuccessText(null);
    setSaving(true);

    try {
      const payload = await apiJson<MePayload>(`/api/people/${personId}`, {
        method: "DELETE",
      });

      setMe(payload);
      setDraft(toDraft(payload));

      const hasAvecNow = payload.people.some((p) => p.is_avec);
      setAvecEnabled(hasAvecNow);

      setSuccessText("Deleted!");
    } catch (e: any) {
      setErrorText(e?.message ?? "Failed to delete");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={22} />
          <Typography color="text.secondary">Loading…</Typography>
        </Stack>
      </Box>
    );
  }

  if (!me) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          Not authenticated. Please go to the login page and try again.
        </Alert>
      </Box>
    );
  }

  console.log(me);

  const people = me.people.filter((p) => !p.created_by_guest);

  return (
    <Box sx={{ p: 3, my: "2rem" }}>
      <Stack spacing={2}>
        <Typography variant="h4">RSVP</Typography>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ lineHeight: "1.5rem", pb: "1rem" }}>
            Dear {people.map((p) => p.full_name).join(" and ")},
          </Typography>
          <Typography>
            We are delighted to invite you to celebrate our wedding with us in
            Hämeenlinna on July 4th, 2026.
            <br />
            Kindly let us know if you will be attending by completing the RSVP
            at your earliest convenience, and no later than <b>May 31st.</b>
          </Typography>

          <Typography sx={{ mt: 1 }}>
            Please read the{" "}
            <RouterLink to="/info">practical wedding info page</RouterLink>{" "}
            before completing the RSVP. Feel free to come back to this RSVP form
            anytime to update your responses.
          </Typography>
        </Paper>
        {/* Add child */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Add children to your RSVP
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <TextField
              label="Child name (optional)"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Age (years, e.g. 3 or 0.5)"
              value={childAge}
              onChange={(e) => setChildAge(e.target.value)}
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={() => void addChild()}
              disabled={saving}
            >
              Add
            </Button>
          </Stack>
        </Paper>

        {/* Avec section */}
        <Paper sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">Plus-one (avec)</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={avecEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      // Don’t allow enabling if rule fails
                      if (checked && !avecAllowed) {
                        setErrorText(
                          "Plus-one is only allowed for households with exactly one adult.",
                        );
                        return;
                      }

                      setErrorText(null);
                      setSuccessText(null);
                      setAvecEnabled(checked);

                      if (!checked) {
                        // if turning off, keep newAvec clean
                        setNewAvec(makeEmptyAvecDraft());
                      }
                    }}
                    disabled={!avecAllowed && !avecEnabled} // allow disabling even when not allowed
                  />
                }
                label={
                  avecEnabled
                    ? "I will bring an avec"
                    : "I will not bring an avec"
                }
              />
            </Stack>

            {avecEnabled ? (
              <>
                <Divider sx={{ my: 1 }} />

                {existingAvec ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography fontWeight={700}>
                          {existingAvec.full_name} (avec)
                        </Typography>
                        <IconButton
                          aria-label="Delete avec"
                          onClick={() =>
                            void deletePerson(
                              existingAvec.id,
                              existingAvec.full_name,
                            )
                          }
                          disabled={saving}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Stack>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <FormControl fullWidth>
                          <InputLabel id={`avec-ceremony-${existingAvec.id}`}>
                            Ceremony
                          </InputLabel>
                          <Select
                            labelId={`avec-ceremony-${existingAvec.id}`}
                            label="Ceremony"
                            value={boolSelectValue(
                              existingAvec.ceremony_attending,
                            )}
                            onChange={(e) =>
                              updatePerson(existingAvec.id, {
                                ceremony_attending: boolFromSelect(
                                  String(e.target.value),
                                ),
                              })
                            }
                          >
                            <MenuItem value="unset">Not answered</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel id={`avec-dinner-${existingAvec.id}`}>
                            Dinner
                          </InputLabel>
                          <Select
                            labelId={`avec-dinner-${existingAvec.id}`}
                            label="Dinner"
                            value={boolSelectValue(
                              existingAvec.dinner_attending,
                            )}
                            onChange={(e) =>
                              updatePerson(existingAvec.id, {
                                dinner_attending: boolFromSelect(
                                  String(e.target.value),
                                ),
                              })
                            }
                          >
                            <MenuItem value="unset">Not answered</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel id={`avec-arrival-${existingAvec.id}`}>
                            Arrival day
                          </InputLabel>
                          <Select
                            labelId={`avec-arrival-${existingAvec.id}`}
                            label="Arrival day"
                            value={existingAvec.arrival ?? ""}
                            onChange={(e) =>
                              updatePerson(existingAvec.id, {
                                arrival: e.target.value
                                  ? (e.target.value as ArrivalDay)
                                  : null,
                              })
                            }
                          >
                            <MenuItem value="">Not answered</MenuItem>
                            {ARRIVAL_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>

                      <TextField
                        label="Allergies / food restrictions"
                        value={existingAvec.dietary_notes}
                        onChange={(e) =>
                          updatePerson(existingAvec.id, {
                            dietary_notes: e.target.value,
                          })
                        }
                        fullWidth
                        multiline
                        minRows={2}
                      />

                      <FormControl fullWidth>
                        <InputLabel id={`avec-alcohol-${existingAvec.id}`}>
                          Alcohol
                        </InputLabel>
                        <Select
                          labelId={`avec-alcohol-${existingAvec.id}`}
                          label="Alcohol"
                          value={existingAvec.alcohol ?? ""}
                          onChange={(e) =>
                            updatePerson(existingAvec.id, {
                              alcohol: e.target.value
                                ? (e.target.value as AlcoholLevel)
                                : null,
                            })
                          }
                        >
                          <MenuItem value="">Not answered</MenuItem>
                          {ALCOHOL_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Divider />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={existingAvec.wants_cottage}
                            disabled={
                              COTTAGES_FULL &&
                              !hadCottageBooking(existingAvec.id)
                            }
                            onChange={(e) =>
                              updatePerson(existingAvec.id, {
                                wants_cottage: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Book cottage accomodation"
                      />
                      {COTTAGES_FULL && !hadCottageBooking(existingAvec.id) && (
                        <Typography variant="body2" color="text.secondary">
                          We have limited number of rooms and beds left, so we
                          have disabled booking in the RSVP to avoid
                          overbooking. Please contact us directly if you would
                          like to stay at the cottages.
                        </Typography>
                      )}

                      {existingAvec.wants_cottage ? (
                        <>
                          <CottageInfo />
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                          >
                            <FormControl fullWidth>
                              <InputLabel
                                id={`avec-cottageStart-${existingAvec.id}`}
                              >
                                Check-in day
                              </InputLabel>
                              <Select
                                labelId={`avec-cottageStart-${existingAvec.id}`}
                                label="Check-in day"
                                value={existingAvec.cottage_start_day ?? ""}
                                onChange={(e) =>
                                  updatePerson(existingAvec.id, {
                                    cottage_start_day: e.target.value
                                      ? (e.target.value as ArrivalDay)
                                      : null,
                                  })
                                }
                              >
                                <MenuItem value="">Not answered</MenuItem>
                                {ARRIVAL_OPTIONS.map((o) => (
                                  <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <TextField
                              label="Room sharing wishes / notes"
                              placeholder="e.g. together with friend X, quiet cottage, party cottage"
                              value={existingAvec.cottage_preferences}
                              onChange={(e) =>
                                updatePerson(existingAvec.id, {
                                  cottage_preferences: e.target.value,
                                })
                              }
                              fullWidth
                            />
                          </Stack>
                        </>
                      ) : null}
                    </Stack>
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <TextField
                          label="Full name"
                          value={newAvec.full_name}
                          onChange={(e) =>
                            setNewAvec((p) => ({
                              ...p,
                              full_name: e.target.value,
                            }))
                          }
                          fullWidth
                        />
                        <TextField
                          label="Phone number (optional)"
                          value={newAvec.phone_e164}
                          onChange={(e) =>
                            setNewAvec((p) => ({
                              ...p,
                              phone_e164: e.target.value,
                            }))
                          }
                          fullWidth
                        />
                      </Stack>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <FormControl fullWidth>
                          <InputLabel id="new-avec-ceremony">
                            Ceremony
                          </InputLabel>
                          <Select
                            labelId="new-avec-ceremony"
                            label="Ceremony"
                            value={boolSelectValue(newAvec.ceremony_attending)}
                            onChange={(e) =>
                              setNewAvec((p) => ({
                                ...p,
                                ceremony_attending: boolFromSelect(
                                  String(e.target.value),
                                ),
                              }))
                            }
                          >
                            <MenuItem value="unset">Not answered</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel id="new-avec-dinner">Dinner</InputLabel>
                          <Select
                            labelId="new-avec-dinner"
                            label="Dinner"
                            value={boolSelectValue(newAvec.dinner_attending)}
                            onChange={(e) =>
                              setNewAvec((p) => ({
                                ...p,
                                dinner_attending: boolFromSelect(
                                  String(e.target.value),
                                ),
                              }))
                            }
                          >
                            <MenuItem value="unset">Not answered</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl fullWidth>
                          <InputLabel id="new-avec-arrival">
                            Arrival day
                          </InputLabel>
                          <Select
                            labelId="new-avec-arrival"
                            label="Arrival day"
                            value={newAvec.arrival ?? ""}
                            onChange={(e) =>
                              setNewAvec((p) => ({
                                ...p,
                                arrival: e.target.value
                                  ? (e.target.value as ArrivalDay)
                                  : null,
                              }))
                            }
                          >
                            <MenuItem value="">Not answered</MenuItem>
                            {ARRIVAL_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>

                      <TextField
                        label="Allergies / food restrictions"
                        value={newAvec.dietary_notes}
                        onChange={(e) =>
                          setNewAvec((p) => ({
                            ...p,
                            dietary_notes: e.target.value,
                          }))
                        }
                        fullWidth
                        multiline
                        minRows={2}
                      />

                      <FormControl fullWidth>
                        <InputLabel id="new-avec-alcohol">Alcohol</InputLabel>
                        <Select
                          labelId="new-avec-alcohol"
                          label="Alcohol"
                          value={newAvec.alcohol ?? ""}
                          onChange={(e) =>
                            setNewAvec((p) => ({
                              ...p,
                              alcohol: e.target.value
                                ? (e.target.value as AlcoholLevel)
                                : null,
                            }))
                          }
                        >
                          <MenuItem value="">Not answered</MenuItem>
                          {ALCOHOL_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Divider />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={newAvec.wants_cottage}
                            disabled={COTTAGES_FULL}
                            onChange={(e) =>
                              setNewAvec((p) => ({
                                ...p,
                                wants_cottage: e.target.checked,
                              }))
                            }
                          />
                        }
                        label="Book cottage accomodation"
                      />
                      {COTTAGES_FULL && (
                        <Typography variant="body2" color="text.secondary">
                          We have limited number of rooms and beds left, so we
                          have disabled booking in the RSVP to avoid
                          overbooking. Please contact us directly if you would
                          like to stay at the cottages.
                        </Typography>
                      )}

                      {newAvec.wants_cottage ? (
                        <>
                          <CottageInfo />
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                          >
                            <FormControl fullWidth>
                              <InputLabel id="new-avec-cottageStart">
                                Check-in day
                              </InputLabel>
                              <Select
                                labelId="new-avec-cottageStart"
                                label="Check-in day"
                                value={newAvec.cottage_start_day ?? ""}
                                onChange={(e) =>
                                  setNewAvec((p) => ({
                                    ...p,
                                    cottage_start_day: e.target.value
                                      ? (e.target.value as ArrivalDay)
                                      : null,
                                  }))
                                }
                              >
                                <MenuItem value="">Not answered</MenuItem>
                                {ARRIVAL_OPTIONS.map((o) => (
                                  <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <TextField
                              label="Room sharing wishes / notes"
                              placeholder="e.g. together with friend X, quiet cottage, party cottage"
                              value={newAvec.cottage_preferences}
                              onChange={(e) =>
                                setNewAvec((p) => ({
                                  ...p,
                                  cottage_preferences: e.target.value,
                                }))
                              }
                              fullWidth
                            />
                          </Stack>
                        </>
                      ) : null}
                    </Stack>
                  </Paper>
                )}
              </>
            ) : null}
          </Stack>
        </Paper>

        {/* People (excluding avec is edited here) */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            People
          </Typography>

          <Stack spacing={2}>
            {nonAvecDraft.map((p) => (
              <Paper key={p.id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography fontWeight={700}>{p.full_name}</Typography>
                      {p.is_child ? (
                        <Typography color="text.secondary">
                          (child
                          {p.child_age_years != null
                            ? `, ${p.child_age_years}y`
                            : ""}
                          )
                        </Typography>
                      ) : null}
                    </Stack>

                    {p.is_child ? (
                      <IconButton
                        aria-label="Delete child"
                        onClick={() => void deletePerson(p.id, p.full_name)}
                        disabled={saving}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    ) : null}
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel id={`ceremony-${p.id}`}>Ceremony</InputLabel>
                      <Select
                        labelId={`ceremony-${p.id}`}
                        label="Ceremony"
                        value={boolSelectValue(p.ceremony_attending)}
                        onChange={(e) =>
                          updatePerson(p.id, {
                            ceremony_attending: boolFromSelect(
                              String(e.target.value),
                            ),
                          })
                        }
                      >
                        <MenuItem value="unset">Not answered</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel id={`dinner-${p.id}`}>Dinner</InputLabel>
                      <Select
                        labelId={`dinner-${p.id}`}
                        label="Dinner"
                        value={boolSelectValue(p.dinner_attending)}
                        onChange={(e) =>
                          updatePerson(p.id, {
                            dinner_attending: boolFromSelect(
                              String(e.target.value),
                            ),
                          })
                        }
                      >
                        <MenuItem value="unset">Not answered</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel id={`arrival-${p.id}`}>
                        Arrival day
                      </InputLabel>
                      <Select
                        labelId={`arrival-${p.id}`}
                        label="Arrival day"
                        value={p.arrival ?? ""}
                        onChange={(e) =>
                          updatePerson(p.id, {
                            arrival: e.target.value
                              ? (e.target.value as ArrivalDay)
                              : null,
                          })
                        }
                      >
                        <MenuItem value="">Not answered</MenuItem>
                        {ARRIVAL_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>

                  <TextField
                    label="Allergies / food restrictions"
                    value={p.dietary_notes}
                    onChange={(e) =>
                      updatePerson(p.id, { dietary_notes: e.target.value })
                    }
                    fullWidth
                    multiline
                    minRows={2}
                  />

                  {!p.is_child ? (
                    <FormControl fullWidth>
                      <InputLabel id={`alcohol-${p.id}`}>Alcohol</InputLabel>
                      <Select
                        labelId={`alcohol-${p.id}`}
                        label="Alcohol"
                        value={p.alcohol ?? ""}
                        onChange={(e) =>
                          updatePerson(p.id, {
                            alcohol: e.target.value
                              ? (e.target.value as AlcoholLevel)
                              : null,
                          })
                        }
                      >
                        <MenuItem value="">Not answered</MenuItem>
                        {ALCOHOL_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}

                  <Divider />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={p.wants_cottage}
                        disabled={COTTAGES_FULL && !hadCottageBooking(p.id)}
                        onChange={(e) =>
                          updatePerson(p.id, {
                            wants_cottage: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Book cottage accomodation"
                  />
                  {COTTAGES_FULL && !hadCottageBooking(p.id) && (
                    <Typography variant="body2" color="text.secondary">
                      We have limited number of rooms and beds left, so we have
                      disabled booking in the RSVP to avoid overbooking. Please
                      contact us directly if you would like to stay at the
                      cottages.
                    </Typography>
                  )}

                  {p.wants_cottage ? (
                    <>
                      <CottageInfo />
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                      >
                        <FormControl fullWidth>
                          <InputLabel id={`cottageStart-${p.id}`}>
                            Check-in day
                          </InputLabel>
                          <Select
                            labelId={`cottageStart-${p.id}`}
                            label="Check-in day"
                            value={p.cottage_start_day ?? ""}
                            onChange={(e) =>
                              updatePerson(p.id, {
                                cottage_start_day: e.target.value
                                  ? (e.target.value as ArrivalDay)
                                  : null,
                              })
                            }
                          >
                            <MenuItem value="">Not answered</MenuItem>
                            {ARRIVAL_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <TextField
                          label="Room sharing wishes / notes"
                          placeholder="e.g. together with friend X, quiet cottage, party cottage"
                          value={p.cottage_preferences}
                          onChange={(e) =>
                            updatePerson(p.id, {
                              cottage_preferences: e.target.value,
                            })
                          }
                          fullWidth
                        />
                      </Stack>
                    </>
                  ) : null}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>

        {saving ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={18} />
            <Typography color="text.secondary">Saving…</Typography>
          </Stack>
        ) : null}

        {errorText ? <Alert severity="error">{errorText}</Alert> : null}
        {successText ? <Alert severity="success">{successText}</Alert> : null}

        {/* Save bar */}

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Typography color="text.secondary" fontStyle="italic">
            You can can edit your RSVP after saving
          </Typography>
          <Button
            variant="contained"
            onClick={() => void saveAll()}
            disabled={!hasChanges || saving}
            size="large"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function CottageInfo() {
  return (
    <Typography sx={{ pb: 2 }}>
      We ask for a €130 fee per adult, which covers accommodation from Thursday,
      July 2nd, 2026 at 16:00 until Monday, July 6th, 2026 at 9:00.
      <br />
      We will send you a booking confirmation along with payment details, which
      you can pay at your convenience before the wedding date.
      <br />
      The price is the same regardless of how many nights you choose to stay and
      includes full access to all amenities at the cottage location.
      <br />
      Couples and families will have their own private room. Single guests may
      be asked to share a room with other guests of the same gender. Rooms have
      3 - 5 beds each, some rooms have double beds.
    </Typography>
  );
}
